/** @file useSheetImport: the Art screen's sheet flows: exploded sheet split, guide sheets, overpaint import. */
import type { IslandMap } from '@zilo/pose3d-image-io/island/islandTypes';
import { decodePng } from '@zilo/pose3d-image-io/png/decodePng';
import type { Image } from '@zilo/pose3d-image-io/types/image';
import { webZlibCodec } from '@zilo/pose3d-image-io/zlib/webZlibCodec';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import { useCallback, useRef, useState } from 'react';
import type { ArtActions } from '../artActions';
import type { ArtDeps, BulkOutcome } from '../artTypes';
import { fillCuts } from './fillCuts';
import { guideSheetOf } from './guideSheetOf';
import { guideRegionsOf } from './guideRegionsOf';
import { loadGuideMasks } from './loadGuideMasks';
import { restPlacementOf } from './restPlacementOf';
import { sheetIslandsOf } from './sheetIslandsOf';
import { sheetKindOf } from './sheetKindOf';
import type { CutPart, GuideSheet } from './sheetTypes';

/** An exploded sheet being split, and a painted guide being cut. */
export type SplitState = { name: string; sheet: Image; map: IslandMap; facingDeg: number };
/** A painted guide being cut. */
export type OverpaintState = { name: string; painted: Image; facingDeg: number };

/** The flows' state and steps; `open` resolves false for a file that is not a sheet of the asked (or named) kind. */
export type SheetImport = {
  split: SplitState | null; overpaint: OverpaintState | null; guideOpen: boolean; result: BulkOutcome | null; busy: boolean;
  open: (file: File, kind?: 'split' | 'overpaint') => Promise<boolean>; openGuide: () => void; close: () => void;
  closeResult: () => void; loadGuide: (deg: number) => Promise<GuideSheet>; fill: (cuts: readonly CutPart[]) => Promise<void>;
};

/** What the flows read: the Art actions and server calls, the open puppet and its revision, and the toasts. */
export type SheetImportInput = {
  actions: Pick<ArtActions, 'fillMany'>; deps: Pick<ArtDeps, 'slotMask'>; puppet: Puppet | null; puppetId: string | null;
  revision: number; notify: (kind: 'error' | 'warning', text: string) => void;
};

/** The file's pixels. */
const decodeFile = async (file: File): Promise<Image> => decodePng(new Uint8Array(await file.arrayBuffer()), webZlibCodec);

/** Opening a file: by its name (`sheet.<deg>.png`, `painted.<deg>.png`) or as `kind` (facing 0 unless named). */
function opener(input: SheetImportInput, set: { split: (s: SplitState) => void; overpaint: (s: OverpaintState) => void }): SheetImport['open'] {
  return async (file, kind) => {
    const named = sheetKindOf(file.name);
    const as = kind ?? named?.kind;
    if (as === undefined) return false;
    const facingDeg = named?.facingDeg ?? 0;
    try {
      const image = await decodeFile(file);
      if (as === 'split') set.split({ name: file.name, sheet: image, map: sheetIslandsOf(image), facingDeg });
      else set.overpaint({ name: file.name, painted: image, facingDeg });
    } catch (e) {
      input.notify('error', `${file.name}: ${e instanceof Error ? e.message : String(e)}`);
    }
    return true;
  };
}

/**
 * The sheet flows over the open puppet. `loadGuide(deg)` builds the guide sheet of a facing with the server's
 * silhouettes (cached per revision and facing, so the overpaint cut uses the very guide that was saved); `fill`
 * sends the cut slot images as one batch and keeps the outcome for the result dialog.
 */
export function useSheetImport(input: SheetImportInput): SheetImport {
  const [split, setSplit] = useState<SplitState | null>(null);
  const [overpaint, setOverpaint] = useState<OverpaintState | null>(null);
  const [guideOpen, setGuideOpen] = useState(false);
  const [result, setResult] = useState<BulkOutcome | null>(null);
  const [busy, setBusy] = useState(false);
  const guides = useRef(new Map<string, Promise<GuideSheet>>());
  const { puppet, puppetId, revision, deps } = input;
  const loadGuide = useCallback((deg: number): Promise<GuideSheet> => {
    const key = `${puppetId ?? ''}|${String(revision)}|${String(deg)}`;
    const held = guides.current.get(key);
    if (held !== undefined) return held;
    if (puppet === null || puppetId === null) return Promise.reject(new Error('no puppet open'));
    const placement = restPlacementOf(puppet, deg);
    const made = loadGuideMasks(deps, puppetId, guideRegionsOf(placement).map((r) => r.part.slot)).then((masks) => guideSheetOf(placement, masks));
    guides.current.set(key, made);
    return made;
  }, [puppet, puppetId, revision, deps]);
  const close = (): void => { setSplit(null); setOverpaint(null); setGuideOpen(false); };
  const fill = async (cuts: readonly CutPart[]): Promise<void> => {
    setBusy(true);
    try {
      const outcome = await fillCuts(input.actions, cuts, (text) => { input.notify('warning', text); });
      close();
      setResult(outcome);
    } finally {
      setBusy(false);
    }
  };
  const open = opener(input, { split: setSplit, overpaint: setOverpaint });
  return { split, overpaint, guideOpen, result, busy, open, openGuide: () => { setGuideOpen(true); }, close, closeResult: () => { setResult(null); }, loadGuide, fill };
}
